// TrainingQuestionsQueue
// Renders the "Training Questions Queue" card — a table of all submitted
// training questions for the selected entity, with per-row approval controls
// for the main developer (approve checkbox, note, save button).
// All state and handlers are owned by ConsoleTraining.tsx and passed in as props.

import React from 'react';
import { TrainingEntityType, TrainingQuestionListItem } from '../../../types';
import { ApprovalDraft } from './types';

interface TrainingQuestionsQueueProps {
  trainingQuestions: TrainingQuestionListItem[];
  loadingTrainingQuestions: boolean;
  entityType: TrainingEntityType;
  selectedEntityId: string;
  requesterEmail: string;
  approvalDrafts: Record<string, ApprovalDraft>;
  savingApprovalQuestionId: string;
  deletingQuestionId: string;
  updateApprovalDraft: (questionId: string, patch: Partial<ApprovalDraft>) => void;
  saveTrainingQuestionApproval: (questionId: string) => Promise<void>;
  handleDeleteTrainingQuestion: (questionId: string) => Promise<void>;
  loadTrainingQuestions: (entityType: TrainingEntityType, entityId: string) => Promise<void>;
}

const TrainingQuestionsQueue: React.FC<TrainingQuestionsQueueProps> = ({
  trainingQuestions,
  loadingTrainingQuestions,
  entityType,
  selectedEntityId,
  requesterEmail,
  approvalDrafts,
  savingApprovalQuestionId,
  deletingQuestionId,
  updateApprovalDraft,
  saveTrainingQuestionApproval,
  handleDeleteTrainingQuestion,
  loadTrainingQuestions,
}) => {
  return (
    <div className="console-card">
      <div className="inline-header">
        <h2>Training Questions Queue</h2>
        <button type="button" onClick={() => void loadTrainingQuestions(entityType, selectedEntityId)}>
          Refresh Questions
        </button>
      </div>
      <p>All table/dashboard questions are curated here. Main developer approval is required at the end.</p>
      {loadingTrainingQuestions ? <p>Loading training questions...</p> : null}

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Submitted At</th>
              <th>Question</th>
              <th>Submitted By</th>
              <th>SQL</th>
              <th>Status</th>
              <th>Main Developer Approval</th>
            </tr>
          </thead>
          <tbody>
            {trainingQuestions.map((row) => {
              const draft = approvalDrafts[row.question_id] || { is_approved: true, approval_note: '' };
              const canApprove = !!requesterEmail && requesterEmail.toLowerCase() === (row.main_developer_email || '').toLowerCase();
              return (
                <tr key={row.question_id}>
                  <td>{row.created_at ? new Date(row.created_at).toLocaleString() : '-'}</td>
                  <td>
                    <div style={{ minWidth: 260 }}>
                      <strong>{row.question}</strong>
                      <div className="column-values">
                        mapping_confirmed={String(row.entity_mapping_confirmed)}, sql_validated={String(row.sql_validated)},
                        is_100_correct={String(row.is_100_correct)}
                      </div>
                    </div>
                  </td>
                  <td>{row.submitted_by_email || '-'}</td>
                  <td>
                    <details>
                      <summary>{row.sql_source || '-'}</summary>
                      <pre style={{ whiteSpace: 'pre-wrap' }}>{row.sql_text || 'No SQL saved'}</pre>
                    </details>
                  </td>
                  <td>
                    {row.main_developer_approved ? 'Approved' : 'Pending'}
                    <div>{row.main_developer_email ? `Main dev: ${row.main_developer_email}` : 'Main dev not set'}</div>
                  </td>
                  <td>
                    <div className="column-fields-stack" style={{ minWidth: 240 }}>
                      <label className="checkbox-row">
                        <input
                          type="checkbox"
                          checked={draft.is_approved}
                          disabled={!canApprove}
                          onChange={(e) => updateApprovalDraft(row.question_id, { is_approved: e.target.checked })}
                        />
                        Approve
                      </label>
                      <label>
                        Note
                        <textarea
                          rows={2}
                          disabled={!canApprove}
                          value={draft.approval_note}
                          onChange={(e) => updateApprovalDraft(row.question_id, { approval_note: e.target.value })}
                        />
                      </label>
                      <button
                        type="button"
                        disabled={!canApprove || savingApprovalQuestionId === row.question_id}
                        onClick={() => void saveTrainingQuestionApproval(row.question_id)}
                      >
                        {savingApprovalQuestionId === row.question_id ? 'Saving...' : 'Save Approval'}
                      </button>
                      <button
                        type="button"
                        disabled={deletingQuestionId === row.question_id}
                        onClick={() => void handleDeleteTrainingQuestion(row.question_id)}
                      >
                        {deletingQuestionId === row.question_id ? 'Deleting...' : 'Delete Question'}
                      </button>
                      {!canApprove ? (
                        <span>
                          {row.main_developer_email
                            ? 'Only main developer can approve'
                            : 'Main developer not assigned'}
                        </span>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
            {!trainingQuestions.length && !loadingTrainingQuestions ? (
              <tr>
                <td colSpan={6}>No training questions submitted for this entity yet.</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TrainingQuestionsQueue;